import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import { getBooleanFromEnv, getVariableFromEnv, getServiceUrl } from "./env";
import { hasRefreshTokenCookie } from "../../authProvider/utils/auth";
import {
  canRefreshToken,
  requestTokenRefresh,
  waitForTokenRefresh,
} from "./tokenRefresh";

type RetriableRequestConfig = InternalAxiosRequestConfig & {
  retriedAfterTokenRefresh?: boolean;
};

axios.defaults.baseURL = getVariableFromEnv("API_BASE_URL");

const logAndReject = (error: AxiosError) => {
  const status = error.response?.status;
  const isExpectedError = status !== undefined && status >= 404 && status < 500;

  if (!isExpectedError && !axios.isCancel(error)) {
    console.log("Unexpected error: " + error, error.response?.status);
  }

  return Promise.reject(error);
};

const refreshWhenCookieAndReject = (error: AxiosError) => {
  if (error.response?.status === 401 && hasRefreshTokenCookie()) {
    requestTokenRefresh();
  }

  return logAndReject(error);
};

axios.interceptors.response.use(undefined, refreshWhenCookieAndReject);

// On-prem keeps the token handling it was validated with.
const addOnPremAuth = (instance: AxiosInstance) => {
  instance.interceptors.request.use((config) => {
    const token = axios.defaults.headers.common.Authorization;
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = token as string;
    }
    return config;
  });

  instance.interceptors.response.use(undefined, refreshWhenCookieAndReject);
};

const addCloudAuth = (instance: AxiosInstance) => {
  // axios.create copies the default headers once, so the token is read per
  // request to pick up one that was refreshed after this client was created.
  instance.interceptors.request.use((config) => {
    const token = axios.defaults.headers.common.Authorization;
    if (token) {
      config.headers.Authorization = token as string;
    }
    return config;
  });

  instance.interceptors.response.use(undefined, async (error: AxiosError) => {
    const config = error.config as RetriableRequestConfig | undefined;
    if (
      error.response?.status !== 401 ||
      !canRefreshToken() ||
      !config ||
      config.retriedAfterTokenRefresh
    ) {
      return logAndReject(error);
    }

    requestTokenRefresh();
    try {
      await waitForTokenRefresh();
    } catch {
      return Promise.reject(error);
    }

    config.retriedAfterTokenRefresh = true;
    return instance(config);
  });
};

export const createHttpClient = (
  service: Parameters<typeof getServiceUrl>[0],
) => {
  const instance = axios.create({
    baseURL: getServiceUrl(service),
  });

  if (getBooleanFromEnv("ON_PREM")) {
    addOnPremAuth(instance);
  } else {
    addCloudAuth(instance);
  }

  return {
    get: instance.get,
    post: instance.post,
    put: instance.put,
    patch: instance.patch,
    delete: instance.delete,
    axios: instance,
  };
};

export default {
  get: axios.get,
  post: axios.post,
  put: axios.put,
  patch: axios.patch,
  delete: axios.delete,
  axios,
};
